// frontend/src/components/AddTradeModal.js
import React, { useEffect, useMemo, useState } from 'react';
import tradingService from '../services/tradingService';

// AddTradeModal: form UI for creating a new trade using dictionaries (symbols/directions) + user inputs.
export default function AddTradeModal({ number, onCancel, onCreated, onError }) {
  // dictionaries state (loaded from API v2)
  const [dictsLoading, setDictsLoading] = useState(false);
  const [dictsError, setDictsError] = useState('');

  const [symbols, setSymbols] = useState([]);
  const [directions, setDirections] = useState([]);

  // form state (matches backend expected payload)
  const [symbol, setSymbol] = useState('');
  const [direction, setDirection] = useState('');
  const [quantity, setQuantity] = useState('');
  const [quoteQuantity, setQuoteQuantity] = useState('');
  const [comment, setComment] = useState('');

  // submit state (POST /trading/account/:number/trade/)
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    const ac = new AbortController();
    let alive = true;

    (async () => {
      try {
        setDictsLoading(true);
        setDictsError('');

        const [symbolsRes, directionsRes] = await Promise.all([
          tradingService.dicts.symbols({ signal: ac.signal }),
          tradingService.dicts.directions({ signal: ac.signal }),
        ]);

        if (!alive) return;

        const symList = symbolsRes?.data?.symbols ?? symbolsRes?.data ?? [];
        const dirList = directionsRes?.data?.directions ?? directionsRes?.data ?? [];

        setSymbols(Array.isArray(symList) ? symList : []);
        setDirections(Array.isArray(dirList) ? dirList : []);

        // helpful defaults (first available options)
        const firstSymbolName = (Array.isArray(symList) && symList[0]?.name) ? String(symList[0].name) : '';
        const firstDirectionName = (Array.isArray(dirList) && dirList[0]?.name) ? String(dirList[0].name) : '';

        setSymbol((prev) => prev || firstSymbolName);
        setDirection((prev) => prev || firstDirectionName);
      } catch (err) {
        if (!alive || ac.signal.aborted) return;
        setSymbols([]);
        setDirections([]);
        setDictsError(err?.message || 'Failed to load dictionaries');
      } finally {
        if (!alive) return;
        setDictsLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, []);

  const canSubmit = useMemo(() => {
    if (!number) return false;
    if (!symbol || !direction) return false;

    const qty = Number(quantity || 0);
    const qqty = Number(quoteQuantity || 0);
    return (qty > 0) || (qqty > 0);
  }, [number, symbol, direction, quantity, quoteQuantity]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || saving) return;

    setSaveError('');
    setSaving(true);

    try {
      const qty = Number(quantity || 0);
      const qqty = Number(quoteQuantity || 0);

      const res = await tradingService.accounts.trades.addForAccount({
        number,
        symbol,
        direction,
        quantity: qty,
        quote_quantity: qqty,
        comment: comment || undefined,
      });

      if (!res?.result) {
        throw new Error(res?.error?.message || 'Trade creation failed');
      }

      onCreated?.(res?.data);
    } catch (err) {
      const msg = err?.message || 'Failed to add trade';
      setSaveError(msg);
      onError?.(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      {dictsError ? <div className="account-details__error">{dictsError}</div> : null}
      {saveError ? <div className="account-details__error">{saveError}</div> : null}
      {dictsLoading ? <div>Loading dictionaries…</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <label>
          <div className="account-details__field-title">Symbol</div>
          <select
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            disabled={dictsLoading || saving}
            className="account-details__control"
          >
            <option value="" disabled>
              Select symbol…
            </option>
            {symbols.map((s) => (
              <option key={String(s?.symbol_id ?? s?.id ?? s?.name)} value={String(s?.name ?? '')}>
                {s?.name ?? '—'}
              </option>
            ))}
          </select>
        </label>

        <label>
          <div className="account-details__field-title">Direction</div>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
            disabled={dictsLoading || saving}
            className="account-details__control"
          >
            <option value="" disabled>
              Select direction…
            </option>
            {directions.map((d) => (
              <option key={String(d?.direction_id ?? d?.id ?? d?.name)} value={String(d?.name ?? '')}>
                {d?.name ?? '—'}
              </option>
            ))}
          </select>
        </label>

        <label>
          <div className="account-details__field-title">Quantity</div>
          <input
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            disabled={saving}
            className="account-details__control"
            inputMode="decimal"
            placeholder="e.g. 0.01"
          />
        </label>

        <label>
          <div className="account-details__field-title">Quote quantity</div>
          <input
            value={quoteQuantity}
            onChange={(e) => setQuoteQuantity(e.target.value)}
            disabled={saving}
            className="account-details__control"
            inputMode="decimal"
            placeholder="e.g. 10 (USDT)"
          />
        </label>

        <label style={{ gridColumn: '1 / -1' }}>
          <div className="account-details__field-title">Comment (optional)</div>
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={saving}
            className="account-details__control"
            placeholder="comment"
          />
        </label>
      </div>

      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" disabled={!canSubmit || saving}>
          {saving ? 'Adding…' : 'Add trade'}
        </button>
      </div>

      <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
        Tip: set <b>quantity</b> or <b>quote quantity</b> (at least one must be &gt; 0).
      </div>
    </form>
  );
}